import React, { useMemo } from 'react';
import { Alert, Button, Col, Empty, Row, Select, Space, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { NEGATIF_RENK, degerMetni, para, sayi, useAktifYukle, yuzde } from './biOrtak';

const { Text } = Typography;

/**
 * Özet sekmesi: /bi-v2/{key}/ozet — sunucuda toplanmış gruplar (Boyut 1 [+ Boyut 2] × ölçü).
 * Satıra / çubuğa tıklayınca grubun değerleri süzgece eklenir ve Satırlar sekmesine geçilir.
 */
export default function BiOzet({ api, rapor, params, boyut1, boyut2, setBoyut1, setBoyut2, olcu, aktif, surum, renk, onDrill }) {
  const boyutSecenek = useMemo(() => (rapor?.kolonlar || []).filter((k) => k.boyut)
    .map((k) => ({ value: k.ad, label: k.ad })), [rapor]);
  const istek = useMemo(() => ({ boyut: boyut1, boyut2: boyut2 || undefined, olcu, limit: 5000, ...params }),
    [boyut1, boyut2, olcu, params]);
  const anahtar = rapor ? JSON.stringify([rapor.key, istek, surum]) : null;
  const { veri, yukleniyor, hata: hataMetni, yeniden } = useAktifYukle(aktif, anahtar,
    () => api.rapor(rapor.key, 'ozet', istek));

  const boyutlar = veri?.boyutlar || [];
  const toplam = veri?.toplam;
  const satirlar = useMemo(() => (veri?.gruplar || []).map((g, i) => {
    const s = { key: i, anahtar: g.anahtar, tutar: g.tutar, adet: g.adet, lokasyon: g.lokasyon,
      pay: toplam?.tutar ? (g.tutar / toplam.tutar) * 100 : null };
    g.anahtar.forEach((v, j) => { s[`k${j}`] = v; });
    return s;
  }), [veri, toplam]);

  const ilk15 = useMemo(() => [...satirlar].sort((a, b) => Math.abs(b.tutar) - Math.abs(a.tutar)).slice(0, 15), [satirlar]);
  const enBuyuk = ilk15.length ? Math.max(...ilk15.map((s) => Math.abs(s.tutar))) || 1 : 1;
  const olcuAdi = veri?.olcu || olcu;

  const kolonlar = [
    ...boyutlar.map((b, j) => ({
      title: b, dataIndex: `k${j}`, key: `k${j}`, width: 220, ellipsis: true,
      sorter: (a, c) => degerMetni(a[`k${j}`]).localeCompare(degerMetni(c[`k${j}`]), 'tr', { numeric: true }),
      render: (v) => (v === null || v === undefined || v === '' ? <Text type="secondary">(Boş)</Text> : String(v)),
    })),
    { title: olcuAdi, dataIndex: 'tutar', key: 'tutar', width: 170, align: 'right', defaultSortOrder: 'descend',
      sorter: (a, c) => a.tutar - c.tutar,
      render: (v) => <span className="nf-amount" style={v < 0 ? { color: NEGATIF_RENK } : undefined}>{para(v)}</span> },
    { title: 'Adet', dataIndex: 'adet', key: 'adet', width: 90, align: 'right', sorter: (a, c) => a.adet - c.adet, render: sayi },
    { title: 'Lokasyon', dataIndex: 'lokasyon', key: 'lokasyon', width: 95, align: 'right',
      sorter: (a, c) => a.lokasyon - c.lokasyon, render: sayi },
    { title: 'Pay %', dataIndex: 'pay', key: 'pay', width: 90, align: 'right',
      sorter: (a, c) => (a.pay || 0) - (c.pay || 0), render: (v) => yuzde(v, 2) },
  ];

  const tikla = (s) => onDrill(boyutlar.map((b, j) => [b, s.anahtar[j]]));

  return (
    <div>
      <Space wrap style={{ marginBottom: 10 }}>
        <Space size={4}><Text>Boyut 1</Text>
          <Select showSearch optionFilterProp="label" value={boyut1} onChange={setBoyut1} options={boyutSecenek}
            style={{ width: 190 }} /></Space>
        <Space size={4}><Text>Boyut 2</Text>
          <Select showSearch allowClear optionFilterProp="label" placeholder="(yok)" value={boyut2 || undefined}
            onChange={(v) => setBoyut2(v || null)} options={boyutSecenek.filter((o) => o.value !== boyut1)}
            style={{ width: 190 }} /></Space>
        <Button icon={<ReloadOutlined />} onClick={yeniden} loading={yukleniyor}>Yeniden yükle</Button>
        <Tooltip title="Satıra ya da çubuğa tıklayınca grubun değerleri süzgece eklenir ve Satırlar sekmesine geçilir.">
          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
        </Tooltip>
      </Space>
      {hataMetni && <Alert type="error" showIcon style={{ marginBottom: 10 }} message="Özet alınamadı" description={hataMetni} />}
      {veri && (
        <Space wrap style={{ marginBottom: 8 }}>
          <Text strong>Σ {olcuAdi}: <span className="nf-amount">{para(toplam?.tutar)}</span></Text>
          <Text type="secondary">{sayi(toplam?.adet)} satır · {sayi(toplam?.lokasyon)} lokasyon · {sayi(veri.grup_sayisi)} grup</Text>
          {veri.grup_sayisi > satirlar.length && (
            <Text type="warning">İlk {sayi(satirlar.length)} grup gösteriliyor; toplam satırı tüm grupları kapsar.</Text>
          )}
        </Space>
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Table rowKey="key" size="small" loading={yukleniyor} columns={kolonlar} dataSource={satirlar}
            scroll={{ x: boyutlar.length * 220 + 445 }} pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 500] }}
            onRow={(s) => ({ onClick: () => tikla(s), style: { cursor: 'pointer' } })}
            locale={{ emptyText: <Empty description={yukleniyor ? 'Yükleniyor…' : 'Kayıt yok'} /> }}
            summary={() => (toplam ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  {boyutlar.map((b, j) => (
                    <Table.Summary.Cell key={b} index={j}>{j === 0 ? <Text strong>Toplam</Text> : null}</Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={boyutlar.length} align="right"><Text strong>{para(toplam.tutar)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={boyutlar.length + 1} align="right"><Text strong>{sayi(toplam.adet)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={boyutlar.length + 2} align="right"><Text strong>{sayi(toplam.lokasyon)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={boyutlar.length + 3} align="right"><Text strong>%100</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>) : null)} />
        </Col>
        <Col xs={24} xl={9}>
          <div className="bi-cubuk-kutu">
            <Text strong style={{ display: 'block', marginBottom: 8 }}>İlk 15 grup (|{olcuAdi}|)</Text>
            {!ilk15.length && <Text type="secondary">Gösterilecek grup yok.</Text>}
            {ilk15.map((s) => (
              <Tooltip key={s.key} title={`${s.anahtar.map(degerMetni).join(' / ')} — ${para(s.tutar)} · ${sayi(s.adet)} satır · ${yuzde(s.pay, 2)}`}>
                <div className="bi-cubuk-satir" onClick={() => tikla(s)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') tikla(s); }}>
                  <div className="bi-cubuk-etiket">{s.anahtar.map(degerMetni).join(' / ')}</div>
                  <div className="bi-cubuk-iz">
                    <div className="bi-cubuk-dolgu"
                      style={{ width: `${Math.max(1, (Math.abs(s.tutar) / enBuyuk) * 100)}%`, background: s.tutar < 0 ? NEGATIF_RENK : renk }} />
                  </div>
                  <div className="bi-cubuk-deger nf-amount">{para(s.tutar)}</div>
                </div>
              </Tooltip>
            ))}
          </div>
        </Col>
      </Row>
    </div>
  );
}
